// Hand-rolled bridge from specs/seeds/dashboard-v2/wizards/recurring-donation-create.yaml
// (CHG-1241 / ITER-3364). See campaign-create.ts header for rationale.

import type { WizardManifest } from "../../wizards/manifestTypes";

export const recurringDonationCreateManifest: WizardManifest = {
  wizard_id: "recurring-donation-create",
  version: "1.0.0",
  spec_id: "experience.cross-screen.recurring-donation-create-v2",
  title: "New recurring donation",
  subtitle: "Set up a recurring gift in 4 steps.",
  shape: "modal-wizard",
  size: "lg",
  persistence_key: "recurring-donation-create",
  trigger: {
    permission: "alora-giving.recurring-donation.create",
    invocation: [
      { from_route: "/recurring", button: "New recurring" },
      { from_route: "/donors/{id}", button: "Set up recurring" },
    ],
  },
  field_type_coverage: {
    canonical: ["donor_picker", "amount", "currency_picker", "designation_picker", "date"],
    pending_primitive: ["text"],
    custom_renders: [
      {
        step_id: "payment",
        reason:
          "payment_method_token is opaque — collected via external payment session (Stripe Setup Intent or PayPal Billing Agreement).",
      },
    ],
  },
  steps: [
    {
      step_id: "donor",
      label: "Donor",
      description: "Who is giving?",
      fields: [{ name: "donor_id", type: "donor_picker", label: "Donor", required: true }],
    },
    {
      step_id: "amount",
      label: "Amount + frequency",
      description: "How much, how often, in which currency.",
      fields: [
        { name: "amount_minor_units", type: "amount", label: "Amount per charge", required: true },
        { name: "currency", type: "currency_picker", label: "Currency", required: true },
        {
          name: "frequency",
          type: "text",
          label: "Frequency",
          required: true,
          pending_primitive: true,
          props: { enum: ["weekly", "monthly", "quarterly", "annually"] },
        },
      ],
    },
    {
      step_id: "schedule",
      label: "Designation + schedule",
      description: "Pick the designation and the start / end policy.",
      fields: [
        { name: "designation_id", type: "designation_picker", label: "Designation", required: true },
        { name: "start_date", type: "date", label: "First charge date", required: true },
        {
          name: "end_policy",
          type: "text",
          label: "End policy",
          required: true,
          pending_primitive: true,
          props: { enum: ["never", "after_n_charges", "until_date"] },
        },
        {
          name: "end_date",
          type: "date",
          label: "End date",
          props: { visible_when: "end_policy == until_date" },
        },
      ],
    },
    {
      step_id: "payment",
      label: "Payment method",
      description: "Authorize the payment method (via external payment provider session).",
      fields: [
        {
          name: "payment_method_token",
          type: "text",
          label: "Payment method token",
          required: true,
          pending_primitive: true,
          props: { masked: true, source: "external-payment-session" },
        },
      ],
    },
  ],
  on_submit: {
    api_endpoint: "POST /recurring-donations",
    on_success: { redirect: "/recurring/{response.id}" },
    on_error: { show_in_step: true },
  },
  validation: [
    { step_id: "donor", field: "donor_id", rule: "not_empty" },
    { step_id: "amount", field: "amount_minor_units", rule: "gt_zero" },
    { step_id: "amount", field: "currency", rule: "iso_4217" },
    { step_id: "amount", field: "frequency", rule: "enum_member" },
    { step_id: "schedule", field: "designation_id", rule: "not_empty" },
    { step_id: "schedule", field: "start_date", rule: "not_in_past" },
    { step_id: "schedule", field: "end_date", rule: "required_when_end_policy_until_date" },
    { step_id: "payment", field: "payment_method_token", rule: "not_empty" },
  ],
};
